import template from 'lodash-es/template';
import uiIcon from '@shared/ui-icon';
import AutofillClickableDropdown from '@shared/autofill-clickable-dropdown';
import selectorTemplate from './templates/selectorTemplate.html';
import selectorItemTemplate from './templates/selectorItemTemplate.html';

const IDENTIFIER = 'job-search-selector';
const MAX_ITEMS_RENDERED = 5;
const ITEM_STATUS = {
  CHECKED: 'checked',
  UNCHECKED: 'unchecked',
};

/**
 * Generates a unique identifier
 * @return {string}  String for the unique identifier.
 */
function generateUniqueIdentifier(title) {
  if (title !== '') {
    return `${IDENTIFIER}-${title}`;
  }
  const identifier = `${IDENTIFIER}-${Math.floor(Math.random() * 1024)}`;
  // Does the pagination element with that identifier exist? Reroll the
  // dice again to get a new unique one.
  if (document.getElementById(identifier)) {
    return generateUniqueIdentifier();
  }

  return identifier;
}

export default class JobSearchSelector {
  /**
   * Job Search Selector
   *
   * @param {HTMLElement} containerEl                      Container to place
   *                                                       the selector
   * @param {String}      [options.title]                  Title for selector
   * @param {Array}       [options.itemList]               List of objects
   * @param {Array}       [options.defaultItemList]        List of strings
   * @param {Function}    [options.onSelectChangeCallback] Callback
   */
  constructor(containerEl, options) {
    // Set-up Variables from options
    this.containerEl = containerEl;
    this.title = options.title || '';
    this.id = generateUniqueIdentifier(this.title);
    this.itemList = options.itemList || [];
    this.defaultItemList = options.defaultItemList || [];
    this.onSelectChangeCallback = options.onSelectChangeCallback;
    // Initialise Variables
    this.selected = []; // Which input/checkboxes are selected
    this.renderedCount = 0; // Counts how many items are rendered

    // Draw base template
    this.containerEl.innerHTML = template(selectorTemplate)({
      id: this.id,
      title: this.title,
      uiIcon,
    });
    // Select elements on the base template
    this.selectListEl = document.getElementById(`${this.id}-selectList`);
    this.autofillFormEl = document.getElementById(`${this.id}-autofillForm`);
    this.autofillAddBtn = document.getElementById(`${this.id}-autofill-addbtn`);

    // Draw Default Checkboxes
    this._renderDefaultItems();

    // Autofill Related Elements
    this.autofillContainerEl = document.getElementById(
      `${this.id}-autofill-container`,
    );
    this.autofillInputEl = document.getElementById(`${this.id}-autofill-input`);
    this.autofillDropdownEl = document.getElementById(
      `${this.id}-autofill-dropdown`,
    );
    // Set up so autofill form El
    this.autofillFormEl.addEventListener('submit', event =>
      this._autofillSubmit(event),
    );
    this.autofillAddBtn.addEventListener('click', event =>
      this._autofillSubmit(event),
    );

    // Creates an Autofill Dropdown
    this.autofillComponent = new AutofillClickableDropdown({
      containerEl: this.autofillContainerEl,
      inputEl: this.autofillInputEl,
      dropdownEl: this.autofillDropdownEl,
      autofillList: this.itemList,
      autofillSearchOptions: { keys: ['name'], threshold: 0.5 },
      autofillDropdownOptions: {
        showTop: 4,
        uniqueWith: (a, b) =>
          a.name.trim().toLowerCase() === b.name.trim().toLowerCase(),
      },
      onListItemSelectCallback: item => {
        this._renderItemToList(item.name, ITEM_STATUS.CHECKED);
        this._pushToSelectedItems(item.name);
        this.autofillInputEl.value = '';
      },
      hideOnFocusOut: true,
    });
  }

  /**
   * Get all items which are selected as a list of initial objects from
   * item list
   * @returns {Array} Array of objects
   */
  getSelectedItems() {
    const allItems = [];
    this.selected.forEach(selectedItem => {
      const itemObject = this.itemList.find(elem => elem.name === selectedItem);
      if (itemObject) {
        allItems.push(itemObject);
      }
    });
    return allItems;
  }

  getItemList() {
    return this.itemList;
  }

  /**
   * Sets a new list of items
   * @param {Array} items Array of Objects
   * @param {Array} defaultItems Names of the default items selected
   */
  setItemList(items, defaultItems = []) {
    // Set Items
    this.itemList = items;
    // Set for Autofill Component
    this.autofillComponent.setAutofillList(items);
    // Set up Default Items
    this.defaultItemList = defaultItems;
    this._renderDefaultItems();
  }

  removeAllSelectedItems() {
    const selectedItems = this.getSelectedItems();
    selectedItems.forEach(item => {
      this._removeSelectedItem(item.name, false);
      this._unsetCheckedStatus(item.name);
    });
  }

  selectItemByName(itemName) {
    const item = this.itemList.find(el => el.name === itemName);
    if (item) {
      this._renderItemToList(item.name, ITEM_STATUS.CHECKED);
      this._pushToSelectedItems(item.name, false);
      this.autofillInputEl.value = '';
    }
  }

  /**
   * Push item onto selected items if it doesn't exist already
   * @param {String} item The item being selected
   */
  _pushToSelectedItems(item, executeCallback = true) {
    const itemIndex = this.selected.indexOf(item);
    if (itemIndex === -1) {
      this.selected.push(item);
    }
    if (executeCallback && typeof this.onSelectChangeCallback === 'function') {
      this.onSelectChangeCallback();
    }
  }

  /**
   * Removes item (and all duplicates if exists) from selected
   * @param {String} item The item being removed
   */
  _removeSelectedItem(item, executeCallback = true) {
    let itemIndex = this.selected.indexOf(item);
    while (itemIndex !== -1) {
      this.selected.splice(itemIndex, 1);
      itemIndex = this.selected.indexOf(item);
    }
    if (executeCallback && typeof this.onSelectChangeCallback === 'function') {
      this.onSelectChangeCallback();
    }
  }

  /**
   * When an item checkbox is clicked
   */
  _onItemClick(event) {
    // Current target is the input element, which has the event listener
    const checkboxInput = event.currentTarget;

    // If checkbox was checked
    if (checkboxInput.checked) {
      // Add it to  selected item
      this._pushToSelectedItems(checkboxInput.getAttribute('data-item'));
    } else {
      this._removeSelectedItem(checkboxInput.getAttribute('data-item'));
    }
  }

  /**
   * Renders an item to list with a given status
   * @param {String} item The item being rendered to the list as a checkbox
   * @param {ITEM_STATUS} status The item status as checked or unchecked
   */
  _renderItemToList(item, itemStatus) {
    // First, iff item is already rendered, just change status
    if (this._isItemRendered(item)) {
      this._setCheckedStatus(item);
      return;
    }

    // If there is already up to max item rendered, then see if we can remove an
    // element from the list
    if (this._getRenderedCount() >= MAX_ITEMS_RENDERED) {
      const elementsRendered = Array.from(
        this.selectListEl.querySelectorAll('li'),
      );
      elementsRendered.reverse().forEach(elem => {
        const elemName = elem.getAttribute('data-item');
        // Try to remove unchecked elements until element count < the max render
        if (
          this._getCheckedStatus(elemName) === false &&
          this._getRenderedCount() >= MAX_ITEMS_RENDERED
        ) {
          elem.remove();
        }
      });
    }
    // Now render the item to the list
    const elementTemplate = template(selectorItemTemplate)({
      id: this.id,
      item,
    });
    this.selectListEl.insertAdjacentHTML('afterbegin', elementTemplate);
    // Set checkbox as checked
    if (itemStatus === ITEM_STATUS.CHECKED) {
      this._setCheckedStatus(item);
    }
    // Add Event Listener
    document
      .getElementById(`${this.id}-${item}`)
      .addEventListener('click', event => this._onItemClick(event));
  }

  /**
   * Gets a count of how many items are rendered
   */
  _getRenderedCount() {
    return this.selectListEl.querySelectorAll('li').length;
  }

  /**
   * Checks if an item is already rendered
   * @param {String} item Name of the item, checks if it's already rendered
   * @returns {Boolean}
   */
  _isItemRendered(item) {
    let exists = false;
    Array.from(this.selectListEl.querySelectorAll('li')).forEach(elem => {
      if (elem.getAttribute('data-item') === item) {
        exists = true;
      }
    });
    return exists;
  }

  /**
   * Draws the default checkboxes
   * Called intially in constructor, and only once.
   * Tries to render up to MAX_ITEMS_RENDERED
   */
  _renderDefaultItems() {
    // Loop through default items and if they exist in the itemList, render
    // them
    // Because render item to list pushes to the list from the top, we should
    // loop through the DefaultItemList in reverse

    this.defaultItemList.reverse().forEach(item => {
      const inArray = this.itemList.find(el => el.name === item);
      // If item was in itemList array and it also hasn't been rendered
      if (inArray && !this._isItemRendered(item)) {
        this._renderItemToList(item, ITEM_STATUS.UNCHECKED);
      }
    });

    // If renderedCount didn't reach MAX_ITEMS_RENDERED, try to add more items
    // from itemList
    this.itemList.forEach(item => {
      if (this._getRenderedCount() < MAX_ITEMS_RENDERED) {
        if (!this._isItemRendered(item.name)) {
          this._renderItemToList(item.name, ITEM_STATUS.UNCHECKED);
        }
      }
    });
  }

  /*
   * Returns whether the checkbox with data-item: item, is checked or not
   * @param {String} item
   */
  _getCheckedStatus(item) {
    return document.getElementById(`${this.id}-${item}`).checked;
  }

  /**
   * Sets status to checked for an item
   */
  _setCheckedStatus(item) {
    document.getElementById(`${this.id}-${item}`).checked = true;
  }

  _unsetCheckedStatus(item) {
    document.getElementById(`${this.id}-${item}`).checked = false;
  }

  /**
   * When autofillDropdown is submitted
   */
  _autofillSubmit(event) {
    event.preventDefault();
    const input = this.autofillInputEl.value;
    const inArray = this.itemList.find(el => el.name === input);
    if (inArray) {
      this._renderItemToList(input, ITEM_STATUS.CHECKED);
      this._pushToSelectedItems(input);
      this.autofillInputEl.value = '';
      return;
    }
    // If submitted doesn't match, select first highlighted item, and show
    // dropdown
    this.autofillComponent.highlightListItem(0);
    this.autofillComponent.showDropdown();
  }
}
